import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEscalationService } from '../services/escalationService';
import { useAuth, type ViewAsRole } from '../../auth/context/AuthContext';
import type { EscalationRequest, EscalationResponse } from '../../../types';
import { EffectiveLevel } from '../../../types/backend';

export const escalationKeys = {
  // Keyed by customer and view-as role: myAccess() is role-dependent server-side
  // (the X-OpsPilot-View-As header), so toggling View-As must be a distinct
  // cache entry that refetches the effective level/ceiling, not a 60s-stale read.
  state: (customerId: string, viewAsRole: ViewAsRole | null) =>
    ['customer', 'escalation-state', customerId, viewAsRole ?? null] as const,
};

interface EscalationState {
  escalations: EscalationResponse[];
  effectiveLevel: string;
  ceiling: string;
}

interface DerivedEscalation {
  activeEscalation: EscalationResponse | null;
  pendingApprovals: EscalationResponse[];
  effectiveLevel: string;
  ceiling: string;
}

const EMPTY_DERIVED: DerivedEscalation = {
  activeEscalation: null,
  pendingApprovals: [],
  effectiveLevel: 'l1',
  ceiling: 'none',
};

// Owns all of EscalationControl's server state: the 60s-polled read plus the
// request/revoke/approve writes. The read lands in the query cache; the derived
// view (my active escalation, pending approvals I can act on) reacts to auth
// changes in memory without a refetch. Writes invalidate the read so the badge
// reflects the new state without a hand-rolled re-fetch.
export function useEscalationControl(customerId: string) {
  const { user, isRealAdmin, viewAsRole } = useAuth();
  const escalationService = useEscalationService();
  const queryClient = useQueryClient();

  // Grants are keyed on the Cognito subject; email is display-only and mutable.
  const userId = user?.sub;

  const query = useQuery({
    queryKey: escalationKeys.state(customerId, viewAsRole),
    queryFn: async (): Promise<EscalationState> => {
      const [escalations, access] = await Promise.all([
        escalationService.listEscalations(customerId),
        escalationService.myAccess(customerId),
      ]);
      return {
        escalations,
        effectiveLevel: access.effective_level ?? 'l1',
        ceiling: access.assigned_level ?? 'none',
      };
    },
    enabled: !!userId,
    refetchInterval: 60_000,
    // Keep the previous customer's badge visible while the new one loads —
    // switching customers must not flash the control away (ADR-0049).
    placeholderData: keepPreviousData,
  });

  const derived = useMemo<DerivedEscalation>(() => {
    const data = query.data;
    if (!data || !userId) return EMPTY_DERIVED;

    const activeEscalation = data.escalations.find(
      e => e.user_id === userId && (e.status === 'active' || e.status === 'pending')
    ) ?? null;

    let pendingApprovals: EscalationResponse[] = [];
    // Mirrors approve-escalation-requires-admin: an admin role, or the admin level.
    const isAnyAdmin = isRealAdmin || user?.assignedLevel === EffectiveLevel.ADMIN;
    if (isAnyAdmin) {
      pendingApprovals = data.escalations.filter(
        e => e.status === 'pending' && (e.user_id !== userId || isRealAdmin)
      );
    }

    return {
      activeEscalation,
      pendingApprovals,
      effectiveLevel: data.effectiveLevel,
      ceiling: data.ceiling,
    };
  }, [query.data, userId, isRealAdmin, user?.assignedLevel]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: escalationKeys.state(customerId, viewAsRole) });

  const requestMutation = useMutation({
    mutationFn: (vars: EscalationRequest) =>
      escalationService.requestEscalation(customerId, vars),
    onSuccess: invalidate,
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => escalationService.revokeEscalation(customerId, userId),
    onSuccess: invalidate,
  });

  const approveMutation = useMutation({
    mutationFn: (userId: string) => escalationService.approveEscalation(customerId, userId),
    onSuccess: invalidate,
  });

  return {
    // isLoading (not isFetching) so only the first load gates render — the 60s
    // background poll must not blank the control. A disabled query (no userId
    // yet) reports isLoading:false, so gate on userId too: pre-auth stays
    // `loading` and never reports a default level upstream before real data.
    loading: !userId || query.isLoading,
    ...derived,
    requestEscalation: requestMutation.mutateAsync,
    revokeEscalation: revokeMutation.mutateAsync,
    approveEscalation: approveMutation.mutateAsync,
    acting: requestMutation.isPending || revokeMutation.isPending,
    approvingUserId: approveMutation.isPending ? approveMutation.variables ?? null : null,
  };
}
